/**
 * Where each dialer view lives. One table for the sidebar, the global
 * search and the quick actions, so a menu entry can no longer point at a
 * route that does not exist (RUI-11: `meetings` and `team` led to the 404).
 */
const VIEW_TO_PATH: Record<string, string> = {
  home: '/ParallelDialer/home',
  queue: '/ParallelDialer',
  followups: '/ParallelDialer/followups',
  // Booking is part of the Agenda tab of Tâches since W4 (B2-followups).
  meetings: '/ParallelDialer/followups?tab=agenda',
  sms: '/ParallelDialer/sms',
  // /team shares the dialer chrome but is not under /ParallelDialer.
  team: '/team',
  analytics: '/ParallelDialer/analytics',
  tools: '/ParallelDialer/tools',
  crm: '/ParallelDialer/crm',
  hubspot: '/ParallelDialer/crm',
  objections: '/ParallelDialer/objections',
  'client-portal': '/ParallelDialer/client-portal',
  settings: '/ParallelDialer/settings',
  'call-history': '/ParallelDialer/call-history',
  billing: '/ParallelDialer/billing',
};

export const DIALER_VIEW_IDS = Object.keys(VIEW_TO_PATH);

/** Path of a dialer view; an unknown view goes to the list rather than a 404. */
export function pathForView(viewId: string): string {
  return VIEW_TO_PATH[viewId] ?? '/ParallelDialer';
}

/**
 * Title and subtitle of each dialer view (`null`: the page draws its own
 * heading). The page title, the <title> and the menu label say the same
 * thing (T-d5, DESIGN.md §5): `VIEW_NAV_LABEL` resolves to the same text,
 * checked by dialerRoutes.test.ts.
 */
export const VIEW_HEADER: Record<string, { title: string; subtitle: string | null } | null> = {
  home:             null,
  queue:            { title: 'pages:dialer.title',       subtitle: 'pages:dialer.subtitle' },
  'call-history':   { title: 'pages:callHistory.title',  subtitle: 'pages:callHistory.subtitle' },
  followups:        { title: 'dialer:nav.tasks',         subtitle: null },
  sms:              { title: 'pages:sms.title',          subtitle: 'pages:sms.subtitle' },
  team:             { title: 'pages:team.title',         subtitle: 'pages:team.subtitle' },
  analytics:        { title: 'pages:analytics.title',    subtitle: 'pages:analytics.subtitle' },
  tools:            { title: 'pages:tools.title',        subtitle: 'pages:tools.subtitle' },
  crm:              { title: 'pages:crm.title',          subtitle: 'pages:crm.subtitle' },
  objections:       { title: 'pages:objections.title',   subtitle: 'pages:objections.subtitle' },
  'client-portal':  { title: 'pages:clientPortal.title', subtitle: 'pages:clientPortal.subtitle' },
  settings:         { title: 'settings:page.title',      subtitle: 'settings:page.subtitle' },
  billing:          { title: 'pages:billing.title',      subtitle: 'pages:billing.subtitle' },
};

/**
 * The CRM page is named after the provider of the active customer (W4,
 * B2-crm, T-d5): « CRM Swoft », « CRM HubSpot », or « CRM » when none is
 * connected (the page is still reachable by URL then). Pass the provider as
 * the screens show it (crmProviderShown).
 */
export function crmTitleKey(provider: string | null | undefined): string {
  if (provider === 'swoft') return 'pages:crm.title';
  if (provider === 'hubspot') return 'pages:crm.titleHubspot';
  return 'pages:crm.titleNone';
}

/** Menu label of each dialer view in the rail (XDialSidebar's TAB_META points at the same keys). */
export const VIEW_NAV_LABEL: Record<string, string> = {
  home: 'dialer:nav.home',
  queue: 'dialer:nav.dialer',
  followups: 'dialer:nav.tasks',
  'call-history': 'dialer:nav.history',
  sms: 'dialer:nav.messaging',
  analytics: 'dialer:nav.analytics',
  // Not in the rail (removed from the menu on 2026-04-20); search and links use this label (W4).
  tools: 'dialer:nav.assistant',
  objections: 'dialer:nav.objections',
  crm: 'dialer:nav.opportunities',
  'client-portal': 'dialer:nav.portal',
  team: 'dialer:nav.team',
  settings: 'dialer:nav.settings',
  billing: 'dialer:nav.billing',
};
